import { useEffect, useState, useRef } from 'react';
import { useSnackbar } from 'notistack';
import { alert } from '../../hooks/function';
import { quizClient } from '../../apis/client-instances';
import QuizResults from './QuizResults';
import QuizAttemptHistory from './QuizAttemptHistory';
import TiptapViewer from '../common/TiptapViewer';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '../ui';

interface QuizPlayerProps {
  lessonId: number;
  enrollmentId?: number;
  passThreshold?: number;
  /** When true (default), the lesson only completes on a passing score.
   *  When false, submitting any attempt completes the lesson. Mirrors
   *  the quiz's `quizSettings.preventProgression`. */
  requirePass?: boolean;
  onQuizPassed?: () => void;
  /** Mark the lesson complete regardless of score — used when the quiz
   *  doesn't require a passing grade. */
  onComplete?: () => void;
}

export default function QuizPlayer({ lessonId, enrollmentId, passThreshold = 80, requirePass = true, onQuizPassed, onComplete }: QuizPlayerProps) {
  const [questions, setQuestions] = useState<any[]>([]);
  const [answers, setAnswers] = useState<Record<number, number[]>>({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [attempts, setAttempts] = useState<any[]>([]);
  const startedAt = useRef(new Date());
  const { enqueueSnackbar } = useSnackbar();

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      const data = await quizClient.getQuestions(lessonId);
      setQuestions(data);
    } catch (error) {
      console.error('Failed to load quiz questions:', error);
      alert(enqueueSnackbar, 'Failed to load quiz', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchAttempts = async () => {
    if (!enrollmentId) return;
    try {
      const data = await quizClient.getAttempts(lessonId, enrollmentId);
      setAttempts(data);
    } catch {
      // silent - attempts are optional display
    }
  };

  useEffect(() => {
    fetchQuestions();
    fetchAttempts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonId, enrollmentId]);

  const handleSingleSelect = (questionId: number, optionIndex: number) => {
    setAnswers((prev) => ({ ...prev, [questionId]: [optionIndex] }));
  };

  const handleMultiSelect = (questionId: number, optionIndex: number, checked: boolean) => {
    setAnswers((prev) => {
      const current = prev[questionId] || [];
      if (checked) {
        return { ...prev, [questionId]: [...current, optionIndex] };
      } else {
        return { ...prev, [questionId]: current.filter((i) => i !== optionIndex) };
      }
    });
  };

  const handleSubmit = async () => {
    // Validate all questions answered
    const unanswered = questions.filter((q) => !answers[q.id] || answers[q.id].length === 0);
    if (unanswered.length > 0) {
      alert(enqueueSnackbar, `Please answer all questions (${unanswered.length} remaining)`, 'warning');
      return;
    }

    if (!enrollmentId) {
      alert(enqueueSnackbar, 'You must be enrolled to submit a quiz', 'error');
      return;
    }

    setSubmitting(true);
    try {
      const attemptResult = await quizClient.submitAttempt({
        enrollmentId,
        lessonId,
        startedAt: startedAt.current,
        answers: questions.map((q) => ({
          questionId: q.id,
          selectedOptionIndexes: answers[q.id] || [],
        })),
      });

      setResult(attemptResult);

      // Completion: when a passing grade is required, only a pass advances
      // the lesson. When it isn't, submitting any attempt completes it.
      if (requirePass) {
        if (attemptResult.passed) onQuizPassed?.();
      } else {
        onComplete?.();
      }

      // Refresh attempts
      fetchAttempts();
    } catch (error) {
      console.error('Failed to submit quiz:', error);
      alert(enqueueSnackbar, 'Failed to submit quiz', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRetake = () => {
    setResult(null);
    setAnswers({});
    startedAt.current = new Date();
  };

  const handleCourseRefresh = () => {
    if (onQuizPassed) onQuizPassed();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (questions.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          This quiz has no questions yet.
        </Typography>
      </Box>
    );
  }

  // Show results if quiz was just submitted
  if (result) {
    return (
      <Box sx={{ p: 3 }}>
        <QuizResults
          score={result.score}
          passed={result.passed}
          totalQuestions={result.totalQuestions}
          correctAnswers={result.correctAnswers}
          passThreshold={passThreshold}
          requirePass={requirePass}
          onRetake={handleRetake}
          onCourseRefresh={handleCourseRefresh}
        />
        <QuizAttemptHistory attempts={attempts} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Pass threshold info */}
      <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
        <Typography variant="body2" color="text.secondary">
          Pass threshold: <strong>{passThreshold}%</strong> &middot; {questions.length} question{questions.length !== 1 ? 's' : ''}
        </Typography>
      </Box>

      {/* Questions */}
      {questions.map((question, qIndex) => (
        <Box
          key={question.id}
          sx={{
            mb: 3,
            p: 3,
            border: '1px solid',
            borderColor: 'grey.200',
            borderRadius: 2,
          }}
        >
          <Box sx={{ display: 'flex', gap: 0.5, mb: 2 }}>
            <Typography variant="body1" fontWeight={600}>
              {qIndex + 1}.
            </Typography>
            <TiptapViewer content={question.questionText} sx={{ flex: 1 }} />
          </Box>

          {/* Question type hint */}
          {question.questionType === 3 && (
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Select all that apply
            </Typography>
          )}

          <FormControl component="fieldset" sx={{ width: '100%' }}>
            {/* TrueFalse or SingleChoice - Radio buttons */}
            {(question.questionType === 1 || question.questionType === 2) && (
              <RadioGroup
                value={answers[question.id]?.[0] ?? ''}
                onChange={(e) => handleSingleSelect(question.id, parseInt(e.target.value))}
              >
                {question.options.map((option: any, oIndex: number) => (
                  <FormControlLabel
                    key={oIndex}
                    value={oIndex}
                    control={<Radio />}
                    label={option.text}
                    sx={{ mb: 0.5 }}
                  />
                ))}
              </RadioGroup>
            )}

            {/* MultipleChoice - Checkboxes */}
            {question.questionType === 3 && (
              <Box>
                {question.options.map((option: any, oIndex: number) => (
                  <FormControlLabel
                    key={oIndex}
                    control={
                      <Checkbox
                        checked={answers[question.id]?.includes(oIndex) || false}
                        onChange={(e) => handleMultiSelect(question.id, oIndex, e.target.checked)}
                      />
                    }
                    label={option.text}
                    sx={{ display: 'block', mb: 0.5 }}
                  />
                ))}
              </Box>
            )}
          </FormControl>
        </Box>
      ))}

      {/* Submit button */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={submitting || !enrollmentId}
          startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : null}
          sx={{ minWidth: 200 }}
        >
          {submitting ? 'Submitting...' : 'Submit Quiz'}
        </Button>
      </Box>

      {/* Previous attempts */}
      <QuizAttemptHistory attempts={attempts} />
    </Box>
  );
}
